import { BoxProps } from '@mui/material';
import { DeliveryStatus } from 'api/models/Deliveries';
import { OrderStatus } from 'api/models/Orders';
import theme from 'App.theme';
import Chip from '../Chip';
import { ReactNode } from 'react';

type Status = DeliveryStatus | OrderStatus;
type ThemeStatus = keyof typeof theme.colors.statuses;

// Some delivery/order statuses are grouped together in the theme, e.g. DISPATCHED and OUTBOUND should both render in purple
// The themeStatusMapping uses types to ensure that we don't miss any delivery or order statuses
// E.g. If you add a new delivery/order status, this will throw an error and you should add it to the mapping
// NOTE: This mapping is different to the one in SetButton.tsx
const themeStatusMapping: Record<Status, ThemeStatus> = {
  // Delivery Status
  ORDERED: 'ORDERED',
  PARTIALLY_DISPATCHED: 'PARTIALLY_DISPATCHED',
  DISPATCHED: 'DISPATCHED',
  OUTBOUND: 'DISPATCHED',
  PARTIALLY_DELIVERED: 'PARTIALLY_DELIVERED',
  DELIVERED: 'DELIVERED',
  RECEIVED: 'RECEIVED',
  OVERDUE_FOR_CHECK_IN: 'RECEIVED',
  PARTIALLY_CHECKED_IN: 'PARTIALLY_CHECKED_IN',
  CHECKED_IN: 'CHECKED_IN',
  OVERDUE_FOR_CHECK_OUT: 'CHECKED_IN',
  PARTIALLY_CHECKED_OUT: 'PARTIALLY_CHECKED_OUT',
  CHECKED_OUT: 'CHECKED_OUT',
  DELETED: 'DELETED',
  CANCELED: 'CANCELED',
  NOT_DELIVERED: 'NOT_DELIVERED',
  // Order Status
  Default: 'ORDERED',
  REQUESTED: 'ORDERED',
  BOOKED: 'ORDERED',
  PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
  RECEIVE: 'RECEIVED',
  INSPECTION: 'RECEIVED',
  CHECK_IN: 'CHECKED_IN',
  CHECK_OUT: 'CHECKED_OUT',
  RETURNED: 'RETURNED',
  PARTIALLY_RETURNED: 'PARTIALLY_RETURNED',
  INBOUND: 'CHECKED_OUT',
};

type Props = {
  status: Status;
  children?: ReactNode;
  allowWrap?: boolean;
} & BoxProps;

export const StatusChip = ({ status, children, allowWrap = false, ...props }: Props) => {
  const themeStatus = themeStatusMapping[status];
  const styles = theme.colors.statuses[themeStatus];

  return (
    <Chip
      label={children || status}
      foregroundColor={styles.foreground}
      backgroundColor={styles.background}
      allowWrap={allowWrap}
      {...props}
    />
  );
};

export default StatusChip;
